/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * The pipeline stages this dashboard has a name for, mirroring the backend's
 * `DEAL_STAGES` (`housien/models/deal/deal_state.py`).
 */
const NAMED_STAGES = new Set([
  'new', 'contacted', 'visiting', 'negotiating',
  'won', 'lost', 'paused',
]);

/**
 * The label for a deal stage, and something readable for one we do not know.
 *
 * Records outlive the pipeline they were written under: `closing` was
 * retired from the stage list but is still stored on older deals, and
 * translating it blindly printed the raw key `dashboard.stages.closing` on
 * the agency's own overview. An unnamed stage now says so and carries its
 * stored value, which is what support needs to see and what a user can
 * repeat back.
 *
 * @param t - Translation function
 * @param stage - The stage stored on the record; may be empty or retired
 * @returns The stage's label, or '' when the record carries no stage
 */
export function stageLabel(t: TFn, stage: string | null | undefined): string {
  if (!stage) return '';
  if (NAMED_STAGES.has(stage)) return t(`dashboard.stages.${stage}`);
  return t('dashboard.stages.unknown', { stage });
}
